import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { type ReactNode } from "react";

import { T } from "@repo/transifex";

import { LogoApplePay } from "../../../svgs/apple-pay";
import { LogoDinersClub } from "../../../svgs/diners-club";
import { LogoMastercard } from "../../../svgs/mastercard";
import { PaymentTerminal } from "../../../svgs/payment-terminal";
import { LogoVisa } from "../../../svgs/visa";
import { Modal } from "../shared/modal";
import { ModalButton } from "../shared/modal-button";
import { ModalTitle } from "../shared/modal-title";

type ModalPaymentProps = {
  onCancel: () => void;
  children: ReactNode;
};

const ModalPaymentRoot = ({ onCancel, children }: ModalPaymentProps) => {
  return (
    <Modal open backdropVariant="idle">
      <div className="flex flex-col gap-y-32">
        <div className="flex flex-col items-center gap-y-16">{children}</div>

        <ModalButton
          onClick={onCancel}
          variant="idle"
          size="large"
          icon={faTimes}
        >
          <T _str="Cancel payment" />
        </ModalButton>
      </div>
    </Modal>
  );
};

const Price = ({ children }: { children: ReactNode }) => (
  <ModalTitle size="9xl" variant="info" weight="bold">
    {children}
  </ModalTitle>
);

const Details = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col items-center gap-y-8">{children}</div>
);

const Instructions = ({ children }: { children: ReactNode }) => (
  <p className="text-6xl font-extralight text-kiosk-text-white">{children}</p>
);

const Terminal = () => <PaymentTerminal className="h-80 w-auto" />;

const CardBrands = () => (
  <div className="flex gap-x-3">
    <LogoVisa />
    <LogoMastercard />
    <LogoDinersClub />
    <LogoApplePay />
  </div>
);

export const ModalPayment = Object.assign(ModalPaymentRoot, {
  Price,
  Details,
  Instructions,
  Terminal,
  CardBrands
});

type PaymentModalVariantProps = {
  price: string;
  onCancel: () => void;
};

export const CardPaymentModal = ({
  price,
  onCancel
}: PaymentModalVariantProps) => (
  <ModalPayment onCancel={onCancel}>
    <ModalPayment.Price>{price}</ModalPayment.Price>
    <ModalPayment.Details>
      <ModalPayment.Instructions>
        <T _str="Tap or insert card to pay" />
      </ModalPayment.Instructions>
      <ModalPayment.CardBrands />
    </ModalPayment.Details>
  </ModalPayment>
);

export const SwishPaymentModal = ({
  price,
  onCancel
}: PaymentModalVariantProps) => (
  <ModalPayment onCancel={onCancel}>
    <ModalPayment.Terminal />
    <ModalPayment.Price>{price}</ModalPayment.Price>
    <ModalPayment.Details>
      <ModalPayment.Instructions>
        <T _str="Scan the QR code on the payment terminal" />
      </ModalPayment.Instructions>
    </ModalPayment.Details>
  </ModalPayment>
);

export const DefaultPaymentModal = ({
  price,
  onCancel
}: PaymentModalVariantProps) => (
  <ModalPayment onCancel={onCancel}>
    <ModalPayment.Price>{price}</ModalPayment.Price>
    <ModalPayment.Details>
      <ModalPayment.Instructions>
        <T _str="Awaiting payment" />
      </ModalPayment.Instructions>
    </ModalPayment.Details>
  </ModalPayment>
);
